import { API_BASE_URL } from '../../../lib/api';
import { apiFetch } from '@/lib/apiFetch';
import { withTenant } from '@/lib/tenantHeaders';

/**
 * Cotizaciones de proveedor del módulo LCL.
 *
 * Los headers se construyen DENTRO de cada función: hacerlo al importar
 * el módulo congela el token y el slug del tenant de la primera carga.
 */
const jsonHeaders = () => withTenant({ 'Content-Type': 'application/json' });

/** Cotizaciones de una solicitud, de la más nueva a la más vieja. */
export async function listLclCotizaciones(lclRequestId) {
  const res = await apiFetch(
    `${API_BASE_URL}/lcl-cotizaciones/by-request/${lclRequestId}`,
    { headers: withTenant() },
  );
  if (!res.ok) throw new Error('No se pudieron cargar las cotizaciones');
  return res.json();
}

export async function getLclCotizacion(id) {
  const res = await apiFetch(`${API_BASE_URL}/lcl-cotizaciones/${id}`, {
    headers: withTenant(),
  });
  if (!res.ok) throw new Error('No se pudo cargar la cotización');
  return res.json();
}

/**
 * Sube el documento del proveedor y devuelve el borrador interpretado.
 * No persiste nada: guardar es un paso aparte del wizard.
 *
 * @param {(pct:number)=>void} onProgress progreso de subida 0..100
 */
export async function extractLclCotizacion({
  lclRequestId,
  file,
  invitationId,
  onProgress,
}) {
  const form = new FormData();
  form.append('file', file);

  const qs = new URLSearchParams({ lclRequestId });
  if (invitationId) qs.set('invitationId', invitationId);
  const url = `${API_BASE_URL}/lcl-cotizaciones/extract?${qs.toString()}`;

  // XHR y no fetch: es la única forma de tener progreso de subida, y en
  // un PDF de varios MB el operador necesita ver que algo pasa.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    const headers = withTenant();
    Object.entries(headers).forEach(([k, v]) => {
      if (v) xhr.setRequestHeader(k, v);
    });
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      let body = {};
      try {
        body = JSON.parse(xhr.responseText || '{}');
      } catch {
        /* respuesta no-JSON: se informa abajo */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body);
      else {
        reject(
          new Error(
            [].concat(body?.message || 'No se pudo procesar el documento').join(' · '),
          ),
        );
      }
    };
    xhr.onerror = () => reject(new Error('Error de red al subir el documento'));
    xhr.send(form);
  });
}

/**
 * Descarta el archivo que quedó en R2 cuando el operador cierra el
 * wizard sin guardar. Falla en silencio a propósito: es limpieza, no
 * puede molestar al usuario que ya se fue.
 */
export async function discardLclUpload(r2Key) {
  if (!r2Key) return;
  try {
    await apiFetch(`${API_BASE_URL}/lcl-cotizaciones/extract/discard`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ r2Key }),
    });
  } catch {
    /* limpieza best-effort */
  }
}

/**
 * Compara las cotizaciones de una solicitud lado a lado.
 *
 * @param {string} lclRequestId
 * @param {object} opts { ids?, monedaComparacion?, tasas? }
 *   `tasas` son unidades de esa moneda por 1 de la moneda de
 *   comparación, igual que una TRM: `{ COP: 3219.31 }`.
 */
export async function compararLclCotizaciones(lclRequestId, opts = {}) {
  const res = await apiFetch(`${API_BASE_URL}/lcl-cotizaciones/comparar`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ lclRequestId, ...opts }),
  });
  return res.json();
}

export async function createLclCotizacion(payload) {
  const res = await apiFetch(`${API_BASE_URL}/lcl-cotizaciones`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function updateLclCotizacion(id, payload) {
  const res = await apiFetch(`${API_BASE_URL}/lcl-cotizaciones/${id}`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function deleteLclCotizacion(id) {
  const res = await apiFetch(`${API_BASE_URL}/lcl-cotizaciones/${id}`, {
    method: 'DELETE',
    headers: withTenant(),
  });
  return res.json();
}

export async function setEstadoLclCotizacion(id, estado) {
  const res = await apiFetch(`${API_BASE_URL}/lcl-cotizaciones/${id}/estado`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ estado }),
  });
  return res.json();
}

/** Marca esta cotización como la elegida y descarta las demás. */
export async function selectLclCotizacion(id) {
  const res = await apiFetch(`${API_BASE_URL}/lcl-cotizaciones/${id}/select`, {
    method: 'POST',
    headers: jsonHeaders(),
  });
  return res.json();
}

/** URL firmada para descargar el documento original del proveedor. */
export async function getLclCotizacionPdfUrl(id) {
  const res = await apiFetch(`${API_BASE_URL}/lcl-cotizaciones/${id}/pdf`, {
    headers: withTenant(),
  });
  return res.json();
}

/**
 * Prepara el payload de guardado a partir del borrador del wizard.
 *
 * El backend acepta los campos que él mismo calcula (totales) para que
 * el borrador se pueda reenviar tal cual, pero los auxiliares con
 * prefijo `_` sí hay que sacarlos: no están en el DTO y el
 * ValidationPipe rechaza el request entero con un 400.
 */
export function buildLclCotizacionPayload(borrador) {
  const payload = { ...borrador, documento: borrador._documento };
  Object.keys(payload).forEach((k) => {
    if (k.startsWith('_')) delete payload[k];
  });
  return payload;
}
